import { ApprovalProcess, ApprovalScopeEnum, Employee, Expense } from "@finway-group/shared/lib/models"
import { getApprovalProcess } from "@finway-group/shared/lib/utils"
import { useState } from "react"
import { EmployeeService } from "~Shared/services"
import { getAbsentApprovers, getAvailableReplacementsForAbsentApprover } from "../utils/employee.utils"
import { useRolesMap } from "./role.hooks"

interface UseReplacementForAbsentApproverParams {
    employees: Array<Employee> | undefined
    checkSubstitutedBy?: boolean
}

interface CheckAbsentApproversParams {
    entity: Pick<Expense, "requestedBy"> & Partial<Pick<Expense, "paymentOption">> & { approvalProcesses?: Array<ApprovalProcess> }
    scope: ApprovalScopeEnum
}

/**
 * Custom hook to manage the replacement of absent approvers workflow.
 * This hook handles the detection of absent approvers and the selection of replacements.
 */
export const useReplacementForAbsentApprover = ({ employees, checkSubstitutedBy = true }: UseReplacementForAbsentApproverParams) => {
    const [isReplacementForAbsentApproverShowing, setIsReplacementForAbsentApproverShowing] = useState(false)
    const [absentApprovers, setAbsentApprovers] = useState<Array<Employee>>([])
    const [availableReplacementsForAbsentApprover, setAvailableReplacementsForAbsentApprover] = useState<Array<Employee>>([])
    const [checkIfApproverInAbsence, setCheckIfApproverInAbsence] = useState(true)
    const [replacementForAbsentApprover, setReplacementForAbsentApprover] = useState<string | undefined>(undefined)

    const rolesMap = useRolesMap({ excludeDeleted: true })

    /**
     * Checks if there are any absent approvers in the approval process.
     * If absent approvers are found (without substitutes), shows the replacement modal.
     *
     * @param entity - The entity to check (e.g., expense, purchase order)
     * @param scope - The approval scope to check (e.g., EXPENSE_PURCHASE, PURCHASE_ORDER)
     * @returns true if absent approvers were found and modal was shown, false otherwise
     */
    const checkForAbsentApprovers = async ({ entity, scope }: CheckAbsentApproversParams): Promise<boolean> => {
        if (!checkIfApproverInAbsence || replacementForAbsentApprover || !entity?.approvalProcesses || !employees) {
            return false
        }

        const approvalProcess = getApprovalProcess(scope, entity.approvalProcesses)
        if (!approvalProcess) {
            return false
        }

        const absentApproversList = getAbsentApprovers(approvalProcess, employees)
        if (absentApproversList.length === 0) {
            return false
        }

        if (checkSubstitutedBy) {
            // substitutedBy only in /employees/:id API
            const absentApproversWithSubstituteDetails = await Promise.all(absentApproversList.map((a) => EmployeeService.fetchEmployeeById(a.id)))
            const absentApproversWithoutSubstitute = absentApproversWithSubstituteDetails.filter((a) => a !== null).filter((a) => !a.substitutedBy)
            if (absentApproversWithoutSubstitute.length > 0) {
                setAvailableReplacementsForAbsentApprover(getAvailableReplacementsForAbsentApprover(entity, employees, rolesMap))
                setAbsentApprovers(absentApproversWithoutSubstitute)
                setIsReplacementForAbsentApproverShowing(true)
                return true
            }
            return false
        }

        // Get available replacements
        const availableReplacements = getAvailableReplacementsForAbsentApprover(entity, employees, rolesMap)
        setAvailableReplacementsForAbsentApprover(availableReplacements)
        setAbsentApprovers(absentApproversList)
        setIsReplacementForAbsentApproverShowing(true)

        return true
    }

    /**
     * Closes the replacement modal and optionally resets the replacement selection.
     */
    const closeModal = () => {
        setIsReplacementForAbsentApproverShowing(false)
    }

    return {
        // State
        isReplacementForAbsentApproverShowing,
        absentApprovers,
        availableReplacementsForAbsentApprover,
        replacementForAbsentApprover,

        // Functions
        checkForAbsentApprovers,
        setCheckIfApproverInAbsence,
        setReplacementForAbsentApprover,
        closeModal,
    }
}
